import React from 'react';
import { Users, DollarSign, TrendingUp, Calculator, CheckCircle, AlertTriangle } from 'lucide-react';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ISR_BRACKETS = [
  { label: 'Exento',   range: '≤ RD$ 416,220',          rate: '0%',  cls: 'text-green-700',  bg: 'bg-green-50'  },
  { label: '15%',      range: '416,220 – 624,329',       rate: '15%', cls: 'text-blue-700',   bg: 'bg-blue-50'   },
  { label: '20%',      range: '624,329 – 867,123',       rate: '20%', cls: 'text-orange-700', bg: 'bg-orange-50' },
  { label: '25%',      range: '> RD$ 867,123',           rate: '25%', cls: 'text-red-700',    bg: 'bg-red-50'    },
];

function getBracket(rentaAnual) {
  if (rentaAnual <= 416220)  return 0;
  if (rentaAnual <= 624329)  return 1;
  if (rentaAnual <= 867123)  return 2;
  return 3;
}

export default function ResumenTab({ ir13, detalles }) {
  const conSaldoFavor = detalles.filter(d => parseFloat(d.saldo_favor) > 0).length;
  const conMontoPagar = detalles.filter(d => parseFloat(d.monto_pagar) > 0).length;

  // Distribución por escala
  const bracketCounts = [0, 0, 0, 0];
  detalles.forEach(d => {
    const renta = parseFloat(d.renta_imponible_anual || 0);
    bracketCounts[getBracket(renta)]++;
  });

  return (
    <div className="space-y-6">

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Users,      label: 'Empleados',       value: ir13.total_empleados,     cls: 'text-blue-600',  fmt: false },
          { icon: DollarSign, label: 'Total Sueldos',   value: ir13.total_sueldos,       cls: 'text-gray-900',  fmt: true  },
          { icon: TrendingUp, label: 'ISR Retenido',    value: ir13.total_isr_retenido,  cls: 'text-gray-900',  fmt: true  },
          { icon: Calculator, label: 'ISR Liquidado',   value: ir13.total_isr_liquidado, cls: 'text-gray-900',  fmt: true  },
        ].map(({ icon: Icon, label, value, cls, fmt: doFmt }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon size={14} className="text-gray-400" />
              <p className="text-xs font-medium text-gray-500">{label}</p>
            </div>
            <p className={`text-base font-bold ${cls}`}>
              {doFmt ? `RD$ ${fmt(value)}` : value}
            </p>
          </div>
        ))}
      </div>

      {/* Resultado de liquidación */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-green-600" />
            <p className="text-sm font-semibold text-green-700">Saldo a Favor del Empleado</p>
          </div>
          <p className="text-2xl font-bold text-green-700">RD$ {fmt(ir13.total_saldo_favor)}</p>
          <p className="text-xs text-green-600 mt-1">{conSaldoFavor} empleado{conSaldoFavor !== 1 ? 's' : ''} con retención en exceso</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-red-600" />
            <p className="text-sm font-semibold text-red-700">Monto por Pagar al Fisco</p>
          </div>
          <p className="text-2xl font-bold text-red-700">RD$ {fmt(ir13.total_monto_pagar)}</p>
          <p className="text-xs text-red-600 mt-1">{conMontoPagar} empleado{conMontoPagar !== 1 ? 's' : ''} con retención insuficiente</p>
        </div>
      </div>

      {/* Distribución por escala */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Distribución por Escala ISR</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {ISR_BRACKETS.map((b, i) => (
            <div key={b.label} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${b.bg} ${b.cls}`}>{b.rate}</span>
                <div>
                  <p className="text-sm font-medium text-gray-700">{b.label}</p>
                  <p className="text-xs text-gray-400">Renta anual: {b.range}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-900">{bracketCounts[i]}</p>
                <p className="text-xs text-gray-400">empleado{bracketCounts[i] !== 1 ? 's' : ''}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
